import { getUTCOffset } from "utils/date";
import { apiQuery } from "utils/queries";
import { logInfo } from "services/logging";
import { api } from "services/restUtil";
import type { UserTimezone } from "services/users/model";

export type PlaceSuggestion = {
  placeId: string;
  description: string;
  types: string[];
  mainText: string;
  secondaryText: string;
};

export type LocationAddress = {
  city: string | null;
  state: string | null;
  country: string | null;
  street_1: string | null;
  street_2: string | null;
  zip: string | null;
};

export type ResolvedLocation = {
  address: LocationAddress;
  timezone: UserTimezone;
};

type LatLng = { lat: number; lng: number };

type AutocompleteQuery = {
  input: string;
  types?: string;
  sessiontoken?: string;
};

type AutocompleteResponse = {
  list: PlaceSuggestion[];
};

export const placesAutocompleteQuery = apiQuery<
  AutocompleteResponse,
  AutocompleteQuery,
  void,
  PlaceSuggestion[]
>({
  key: "/maps/places/autocomplete",
  select: response => response.list,
});

export async function detectCurrentLocation(): Promise<ResolvedLocation> {
  if (!navigator.geolocation) {
    throw new Error("Geolocation is not supported by this browser");
  }

  try {
    const { coords } = await new Promise<GeolocationPosition>((resolve, reject) =>
      navigator.geolocation.getCurrentPosition(resolve, reject)
    );

    const { address, coordinates } = await reverseGeocodeCoordinates({
      lat: coords.latitude,
      lng: coords.longitude,
    });

    return {
      address,
      timezone: await fetchTimezoneForCoordinates(coordinates),
    };
  } catch (error) {
    logInfo("error when detecting user's location", error);
    throw error;
  }
}

type TimezoneResult = {
  id: string;
  rawOffset: number;
  dstOffset: number;
};

export async function fetchTimezoneForCoordinates(latLng: LatLng): Promise<UserTimezone> {
  const result = await api.get<TimezoneResult>("/maps/timezone", {
    lat: latLng.lat,
    lng: latLng.lng,
    timestamp: Math.floor(Date.now() / 1000),
  });

  return {
    id: result.id,
    utc_offset: getUTCOffset(result.id),
  };
}

export type AddressLocation = {
  address: LocationAddress;
  coordinates: LatLng;
};

export function geocodeAddress(address: string): Promise<AddressLocation> {
  return api.get<AddressLocation>("/maps/geocode", { address });
}

export function reverseGeocodeCoordinates(coordinates: LatLng): Promise<AddressLocation> {
  return api.get<AddressLocation>("/maps/geocode", {
    lat: coordinates.lat,
    lng: coordinates.lng,
  });
}

export type LocationSummary = {
  city: string | null;
  state: string | null;
  country: string | null;
};

export function getAddressDescription(
  address: LocationSummary | LocationAddress | null | undefined
) {
  return [address?.city, address?.state, address?.country].filter(Boolean).join(", ");
}
